import {
  getGroq,
  GROQ_MODELS,
  reviewDocumentWithGroq,
  improveDocumentWithGroq,
} from "../groq-client";
import {
  getSystemPrompt,
  getDocumentPrompt,
  COUNTRY_CONTEXTS,
} from "../prompts/base-prompts";

function stripGeneratedNotes(content: string): string {
  return content
    .replace(
      /\n*\s*(NOTA|NOTE)\s*:\s*Este documento ha sido generado con asistencia de inteligencia artificial\.[\s\S]*$/i,
      "",
    )
    .trim();
}


export interface GenerateDocumentParams {
  documentType: string;
  country: keyof typeof COUNTRY_CONTEXTS;
  data: Record<string, any>;
}

export interface GeneratedDocument {
  content: string;
  title: string;
  documentType: string;
  country: string;
}

export const generateDocument = async ({
  documentType,
  country,
  data,
}: GenerateDocumentParams): Promise<GeneratedDocument> => {
  try {
    const systemPrompt = getSystemPrompt(country);
    const userPrompt = getDocumentPrompt(documentType, country, data);

    const groq = getGroq();
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt || "" },
      ],
      temperature: 0.7,
      max_tokens: 8000,
    });

    const content = stripGeneratedNotes(
      completion.choices[0]?.message?.content?.trim() || "",
    );
    if (!content) {
      throw new Error("No se pudo generar el documento");
    }

    const title = data.title || `${documentType}-${Date.now()}`;

    return {
      content,
      title,
      documentType,
      country,
    };
  } catch (error) {
    console.error("Error generating document:", error);
    if (error instanceof Error) {
      if (error.message.includes("rate limit") || error.message.includes("429")) {
        throw new Error(
          "Límite de cuota excedido. Por favor, intenta más tarde."
        );
      }
      throw new Error(`Error al generar el documento: ${error.message}`);
    }
    throw new Error(
      "Error al generar el documento. Por favor, intenta nuevamente."
    );
  }
};

export const reviewDocument = async (
  documentContent: string,
  country: keyof typeof COUNTRY_CONTEXTS
): Promise<string> => {
  const { suggestions } = await reviewDocumentWithGroq(
    documentContent,
    country as string
  );
  return suggestions;
};

export interface ImproveDocumentParams {
  documentContent: string;
  country: keyof typeof COUNTRY_CONTEXTS;
  instructions?: string;
}

export const improveDocument = async ({
  documentContent,
  country,
  instructions,
}: ImproveDocumentParams): Promise<string> => {
  return improveDocumentWithGroq(
    documentContent,
    country as string,
    instructions
  );
};
